import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import 'iconify-icon';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import FileUploader from '../../components/admin/FileUploader';
import { STATES_LIST, resolveState } from '../../data/brazil-state-centroids';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Generates a stable local-only identifier used as React `key` for items
// inside the dynamic nested editors (testimonials, cases, team). Stripped
// before submitting to the API.
const newUid = () => `_${Math.random().toString(36).slice(2, 11)}`;
const withUid = (item) => (item && item._uid ? item : { ...item, _uid: newUid() });
const stripUid = ({ _uid, ...rest }) => rest;

const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const blankForm = {
  name: '',
  legal_name: '',
  city: '',
  state: '',
  contact_email: '',
  instagram: '',
  whatsapp: '',
  address: '',
  cities_servedRaw: '',
  testimonials: [], // [{name, role, text}]
  cases: [], // [{company, description, image}]
  team: [], // [{name, role, photo, instagram}]
  // Etapa 7 — campos novos
  latitude: '',
  longitude: '',
  products_offered: [], // slug list
  format: '',
  partners_raw: '',
  instagram_handles_raw: '',
};

const PRODUCT_OPTIONS = [
  { slug: 'e-personalidade', name: 'E-Personalidade' },
  { slug: 'e-processual', name: 'E-Processual' },
  { slug: 'e-corpo', name: 'E-Corpo' },
  { slug: 'e-vendas', name: 'E-Vendas' },
  { slug: 'e-got', name: 'E-GOT' },
];

const FORMAT_OPTIONS = ['Franquia', 'Filial'];

const STATES = STATES_LIST; // { code, name }[] — backend stores `state` as 2-letter code

const FranchisesManager = () => {
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFranchise, setEditingFranchise] = useState(null);
  const [formData, setFormData] = useState(blankForm);
  const [searchParams, setSearchParams] = useSearchParams();
  const [geocoding, setGeocoding] = useState(false);

  useEffect(() => { loadFranchises(); }, []);

  // Auto-open the edit dialog for the franchise specified via ?edit=<id>.
  // Used by the floating "Adicionar foto" button on the public /equipe page.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || !franchises.length) return;
    const f = franchises.find((x) => x.franchise_id === editId);
    if (f) {
      handleEdit(f);
      // Scroll to the Eneagramados section once the dialog opens.
      setTimeout(() => {
        const sec = document.querySelector('[data-testid="franchise-add-trainer"]');
        sec?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
      // Drop the query param so refresh doesn't loop.
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
  }, [franchises, searchParams, setSearchParams]);

  const loadFranchises = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/admin/franchises`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      setFranchises(data);
    } catch (e) {
      toast.error('Erro ao carregar franquias');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      // Strip the local _uid markers used only for React keys
      testimonials: (formData.testimonials || []).map(stripUid),
      cases: (formData.cases || []).map(stripUid),
      team: (formData.team || []).map(stripUid),
      cities_served: formData.cities_servedRaw
        .split(/[,;]+/).map((s) => s.trim()).filter(Boolean),
      partners: formData.partners_raw
        .split(/[,;]+/).map((s) => s.trim()).filter(Boolean),
      instagram_handles: formData.instagram_handles_raw
        .split(/[,;]+/).map((s) => s.trim()).filter(Boolean),
      latitude: formData.latitude !== '' ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude !== '' ? parseFloat(formData.longitude) : null,
      products_offered: formData.products_offered || [],
      format: formData.format || null,
    };
    delete payload.cities_servedRaw;
    delete payload.partners_raw;
    delete payload.instagram_handles_raw;
    try {
      if (editingFranchise) {
        await axios.put(
          `${API_URL}/admin/franchises/${editingFranchise.franchise_id}`,
          payload,
          { withCredentials: true, headers: authHeaders() },
        );
        toast.success('Franquia atualizada com sucesso!');
      } else {
        await axios.post(`${API_URL}/admin/franchises`, payload, {
          withCredentials: true, headers: authHeaders(),
        });
        toast.success('Franquia criada com sucesso!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadFranchises();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar franquia');
    }
  };

  const handleEdit = (franchise) => {
    setEditingFranchise(franchise);
    setFormData({
      name: franchise.name || `IE ${franchise.city || ''}`.trim(),
      legal_name: franchise.legal_name || '',
      city: franchise.city || '',
      state: resolveState(franchise.state)?.code || '',
      contact_email: franchise.contact_email || '',
      instagram: franchise.instagram || '',
      whatsapp: franchise.whatsapp || franchise.phone || '',
      address: franchise.address || '',
      cities_servedRaw: (franchise.cities_served || []).join(', '),
      testimonials: (franchise.testimonials || []).map(withUid),
      cases: (franchise.cases || []).map(withUid),
      team: (franchise.team || []).map(withUid),
      latitude: franchise.latitude != null ? String(franchise.latitude) : '',
      longitude: franchise.longitude != null ? String(franchise.longitude) : '',
      products_offered: franchise.products_offered || [],
      format: franchise.format || '',
      partners_raw: (franchise.partners || []).join(', '),
      instagram_handles_raw: (franchise.instagram_handles || []).join(', '),
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta franquia?')) return;
    try {
      await axios.delete(`${API_URL}/admin/franchises/${id}`, {
        withCredentials: true, headers: authHeaders(),
      });
      toast.success('Franquia excluída!');
      loadFranchises();
    } catch (e) {
      toast.error('Erro ao excluir franquia');
    }
  };

  const resetForm = () => {
    setFormData(blankForm);
    setEditingFranchise(null);
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // Auto-geocode via OpenStreetMap Nominatim (free, no key). Uses the form's
  // current city + state to fill latitude/longitude.
  const autoGeocode = async () => {
    if (!formData.city) {
      toast.error('Preencha a cidade primeiro.');
      return;
    }
    setGeocoding(true);
    try {
      const stateMeta = resolveState(formData.state);
      const query = encodeURIComponent(
        `${formData.city}, ${stateMeta?.name || formData.state || ''}, Brasil`.replace(/, ,/g, ','),
      );
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1&countrycodes=br`,
        { headers: { Accept: 'application/json' } },
      );
      const data = await res.json();
      if (!Array.isArray(data) || data.length === 0) {
        toast.error('Localização não encontrada. Verifique cidade/estado ou preencha manualmente.');
        return;
      }
      const { lat, lon } = data[0];
      setFormData((p) => ({
        ...p,
        latitude: parseFloat(lat).toFixed(4),
        longitude: parseFloat(lon).toFixed(4),
      }));
      toast.success(`Coordenadas encontradas: ${parseFloat(lat).toFixed(4)}, ${parseFloat(lon).toFixed(4)}`);
    } catch (err) {
      toast.error('Falha ao consultar OpenStreetMap. Tente novamente.');
    } finally {
      setGeocoding(false);
    }
  };

  // ---- nested editors ----
  const addTestimonial = () =>
    setFormData((p) => ({ ...p, testimonials: [...p.testimonials, { _uid: newUid(), name: '', role: '', text: '' }] }));
  const updateTestimonial = (i, key, value) =>
    setFormData((p) => ({
      ...p,
      testimonials: p.testimonials.map((t, idx) => (idx === i ? { ...t, [key]: value } : t)),
    }));
  const removeTestimonial = (i) =>
    setFormData((p) => ({ ...p, testimonials: p.testimonials.filter((_, idx) => idx !== i) }));

  const addCase = () =>
    setFormData((p) => ({ ...p, cases: [...p.cases, { _uid: newUid(), company: '', description: '', image: '' }] }));
  const updateCase = (i, key, value) =>
    setFormData((p) => ({
      ...p,
      cases: p.cases.map((c, idx) => (idx === i ? { ...c, [key]: value } : c)),
    }));
  const removeCase = (i) =>
    setFormData((p) => ({ ...p, cases: p.cases.filter((_, idx) => idx !== i) }));

  // ---- team / trainers ----
  const addTrainer = () =>
    setFormData((p) => ({
      ...p,
      team: [...p.team, { _uid: newUid(), name: '', role: 'Trainer', photo: '', instagram: '' }],
    }));
  const updateTrainer = (i, key, value) =>
    setFormData((p) => ({
      ...p,
      team: p.team.map((m, idx) => (idx === i ? { ...m, [key]: value } : m)),
    }));
  const removeTrainer = (i) =>
    setFormData((p) => ({ ...p, team: p.team.filter((_, idx) => idx !== i) }));

  const franchisesByState = franchises.reduce((acc, f) => {
    if (!acc[f.state]) acc[f.state] = [];
    acc[f.state].push(f);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-franchises">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-bold mb-2">Franquias</h2>
          <p className="text-neutral-600">Gerencie as unidades franqueadas com todos os dados públicos.</p>
        </div>
        <Button
          onClick={() => { resetForm(); setIsDialogOpen(true); }}
          className="flex items-center gap-2"
          data-testid="franchise-new-btn"
        >
          <iconify-icon icon="solar:add-circle-linear"></iconify-icon>
          Nova Franquia
        </Button>
      </div>

      <div className="space-y-6">
        {Object.entries(franchisesByState).map(([state, list]) => (
          <div key={state} className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
            <div className="bg-neutral-50 px-6 py-4 border-b border-neutral-200">
              <h3 className="font-bold text-lg">{state} ({list.length})</h3>
            </div>
            <div className="divide-y divide-neutral-200">
              {list.map((f) => (
                <div key={f.franchise_id} className="p-6 hover:bg-neutral-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-lg mb-1">{f.name || f.city}</h4>
                      {f.legal_name && (
                        <p className="text-xs text-zinc-700 mb-2">{f.legal_name}</p>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm text-neutral-600">
                        <p className="flex items-center gap-2 truncate">
                          <iconify-icon icon="solar:letter-linear"></iconify-icon>
                          <span className="truncate">{f.contact_email}</span>
                        </p>
                        {f.instagram && (
                          <p className="flex items-center gap-2">
                            <iconify-icon icon="solar:instagram-linear"></iconify-icon>
                            {f.instagram}
                          </p>
                        )}
                        {(f.whatsapp || f.phone) && (
                          <p className="flex items-center gap-2">
                            <iconify-icon icon="mdi:whatsapp"></iconify-icon>
                            {f.whatsapp || f.phone}
                          </p>
                        )}
                        {f.cities_served?.length > 0 && (
                          <p className="flex items-center gap-2 truncate">
                            <iconify-icon icon="solar:map-point-linear"></iconify-icon>
                            <span className="truncate">{f.cities_served.join(' · ')}</span>
                          </p>
                        )}
                      </div>
                      <div className="flex gap-3 mt-2 text-xs text-zinc-700">
                        <span>{f.team?.length || 0} Eneagramado(s)</span>
                        <span>·</span>
                        <span>{f.testimonials?.length || 0} depoimento(s)</span>
                        <span>·</span>
                        <span>{f.cases?.length || 0} case(s)</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => handleEdit(f)} className="p-2 text-zinc-700 hover:text-accent-orange hover:bg-neutral-200 rounded-lg transition-colors" aria-label="Editar" title="Editar">
                        <iconify-icon icon="solar:pen-linear" className="text-xl"></iconify-icon>
                      </button>
                      <button onClick={() => handleDelete(f.franchise_id)} className="p-2 text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-colors" aria-label="Excluir" title="Excluir">
                        <iconify-icon icon="solar:trash-bin-linear" className="text-xl"></iconify-icon>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFranchise ? 'Editar Franquia' : 'Nova Franquia'}</DialogTitle>
            <DialogDescription>
              Preencha as informações públicas da franquia. Depoimentos e cases podem ser adicionados dinamicamente.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="franchise-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Nome da Franquia *</Label>
                <Input id="name" name="name" required value={formData.name} onChange={handleChange} placeholder="IE Porto Alegre" />
              </div>
              <div>
                <Label htmlFor="legal_name">Razão Social</Label>
                <Input id="legal_name" name="legal_name" value={formData.legal_name} onChange={handleChange} placeholder="IE Porto Alegre Treinamentos Ltda." />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="state">Estado *</Label>
                <select
                  id="state" name="state" required value={formData.state} onChange={handleChange}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm outline-none focus:border-neutral-500"
                >
                  <option value="">Selecione…</option>
                  {STATES.map((s) => <option key={s.code} value={s.code}>{s.code} — {s.name}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="city">Cidade-sede</Label>
                <Input id="city" name="city" value={formData.city} onChange={handleChange} placeholder="Porto Alegre" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_email">E-mail *</Label>
                <Input id="contact_email" name="contact_email" type="email" required value={formData.contact_email} onChange={handleChange} />
              </div>
              <div>
                <Label htmlFor="whatsapp">WhatsApp</Label>
                <Input id="whatsapp" name="whatsapp" value={formData.whatsapp} onChange={handleChange} placeholder="(51) 99999-9999" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="instagram">Instagram</Label>
                <Input id="instagram" name="instagram" value={formData.instagram} onChange={handleChange} placeholder="@ie_cidade" />
              </div>
              <div>
                <Label htmlFor="address">Endereço</Label>
                <Input id="address" name="address" value={formData.address} onChange={handleChange} />
              </div>
            </div>

            <div>
              <Label htmlFor="cities_servedRaw">Principais Cidades de Atuação</Label>
              <Input id="cities_servedRaw" name="cities_servedRaw" value={formData.cities_servedRaw} onChange={handleChange} placeholder="Porto Alegre, Canoas, Gravataí" />
              <p className="text-xs text-neutral-500 mt-1">Separe por vírgula ou ponto-e-vírgula.</p>
            </div>

            {/* === Etapa 7: Coordenadas / Formato / Produtos / Sócios / IGs === */}
            <div className="border-t border-neutral-200 pt-5 space-y-4">
              <h4 className="font-semibold text-sm text-neutral-700 uppercase tracking-wider">
                Dados estendidos
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="format">Formato</Label>
                  <select
                    id="format"
                    name="format"
                    value={formData.format}
                    onChange={handleChange}
                    data-testid="franchise-form-format"
                    className="w-full border border-neutral-300 rounded-md p-2 text-sm bg-white"
                  >
                    <option value="">—</option>
                    {FORMAT_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="latitude">Latitude</Label>
                  <Input
                    id="latitude"
                    name="latitude"
                    type="number"
                    step="0.0001"
                    value={formData.latitude}
                    onChange={handleChange}
                    placeholder="-30.0346"
                    data-testid="franchise-form-latitude"
                  />
                </div>
                <div>
                  <Label htmlFor="longitude">Longitude</Label>
                  <Input
                    id="longitude"
                    name="longitude"
                    type="number"
                    step="0.0001"
                    value={formData.longitude}
                    onChange={handleChange}
                    placeholder="-51.2177"
                    data-testid="franchise-form-longitude"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between -mt-2 mb-2">
                <p className="text-xs text-neutral-500">
                  Use coordenadas precisas da cidade-sede (Google Maps: clique direito → copiar lat/lng).
                </p>
                <button
                  type="button"
                  onClick={autoGeocode}
                  disabled={!formData.city || geocoding}
                  data-testid="franchise-form-geocode"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest rounded-full border border-accent-orange text-accent-orange hover:bg-accent-orange hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <iconify-icon
                    icon={geocoding ? 'svg-spinners:180-ring-with-bg' : 'solar:map-arrow-square-linear'}
                  ></iconify-icon>
                  {geocoding ? 'Buscando…' : 'Auto-localizar'}
                </button>
              </div>

              <div>
                <Label>Programas oferecidos</Label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="franchise-form-products">
                  {PRODUCT_OPTIONS.map((p) => {
                    const checked = formData.products_offered?.includes(p.slug);
                    return (
                      <button
                        key={p.slug}
                        type="button"
                        onClick={() => {
                          setFormData((prev) => {
                            const set = new Set(prev.products_offered || []);
                            if (set.has(p.slug)) set.delete(p.slug);
                            else set.add(p.slug);
                            return { ...prev, products_offered: Array.from(set) };
                          });
                        }}
                        data-testid={`franchise-form-product-${p.slug}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border transition-colors ${
                          checked
                            ? 'bg-accent-orange text-white border-accent-orange'
                            : 'bg-white text-neutral-600 border-neutral-300 hover:border-accent-orange'
                        }`}
                      >
                        {checked && (
                          <iconify-icon icon="solar:check-circle-bold" className="mr-1"></iconify-icon>
                        )}
                        {p.name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  Clique para alternar. Aparece como tags no perfil público da franquia.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="partners_raw">Sócios</Label>
                  <Input
                    id="partners_raw"
                    name="partners_raw"
                    value={formData.partners_raw}
                    onChange={handleChange}
                    placeholder="Jorge, Heloisa"
                    data-testid="franchise-form-partners"
                  />
                  <p className="text-xs text-neutral-500 mt-1">Separe por vírgula.</p>
                </div>
                <div>
                  <Label htmlFor="instagram_handles_raw">Instagram (múltiplos)</Label>
                  <Input
                    id="instagram_handles_raw"
                    name="instagram_handles_raw"
                    value={formData.instagram_handles_raw}
                    onChange={handleChange}
                    placeholder="@ie_cidade1, @ie_cidade2"
                    data-testid="franchise-form-iglist"
                  />
                  <p className="text-xs text-neutral-500 mt-1">
                    Quando a franquia opera com mais de um perfil regional.
                  </p>
                </div>
              </div>
            </div>

            {/* Depoimentos */}
            <div className="border-t border-neutral-200 pt-5">
              <div className="flex items-center justify-between mb-3">
                <Label>Depoimentos de clientes</Label>
                <Button type="button" variant="outline" size="sm" onClick={addTestimonial}>
                  <iconify-icon icon="solar:add-circle-linear" className="mr-1"></iconify-icon>
                  Adicionar
                </Button>
              </div>
              <div className="space-y-3">
                {formData.testimonials.map((t, i) => (
                  <div key={t._uid || `t-${i}`} className="p-3 bg-neutral-50 rounded-lg space-y-2 relative">
                    <button
                      type="button"
                      onClick={() => removeTestimonial(i)}
                      className="absolute top-2 right-2 p-1 hover:bg-red-100 text-red-600 rounded"
                      aria-label="Remover"
                    >
                      <iconify-icon icon="solar:trash-bin-linear"></iconify-icon>
                    </button>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pr-8">
                      <Input placeholder="Nome" value={t.name} onChange={(e) => updateTestimonial(i, 'name', e.target.value)} />
                      <Input placeholder="Cargo/Empresa (opcional)" value={t.role || ''} onChange={(e) => updateTestimonial(i, 'role', e.target.value)} />
                    </div>
                    <textarea
                      placeholder="Texto do depoimento"
                      value={t.text}
                      onChange={(e) => updateTestimonial(i, 'text', e.target.value)}
                      rows="2"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm outline-none focus:border-neutral-500 resize-none"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Cases */}
            <div className="border-t border-neutral-200 pt-5">
              <div className="flex items-center justify-between mb-3">
                <Label>Cases corporativos</Label>
                <Button type="button" variant="outline" size="sm" onClick={addCase}>
                  <iconify-icon icon="solar:add-circle-linear" className="mr-1"></iconify-icon>
                  Adicionar
                </Button>
              </div>
              <div className="space-y-3">
                {formData.cases.map((c, i) => (
                  <div key={c._uid || `c-${i}`} className="p-3 bg-neutral-50 rounded-lg space-y-2 relative">
                    <button
                      type="button"
                      onClick={() => removeCase(i)}
                      className="absolute top-2 right-2 p-1 hover:bg-red-100 text-red-600 rounded"
                      aria-label="Remover"
                    >
                      <iconify-icon icon="solar:trash-bin-linear"></iconify-icon>
                    </button>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pr-8">
                      <Input placeholder="Empresa" value={c.company} onChange={(e) => updateCase(i, 'company', e.target.value)} />
                      <Input placeholder="URL da logo (opcional)" value={c.image || ''} onChange={(e) => updateCase(i, 'image', e.target.value)} />
                    </div>
                    <textarea
                      placeholder="Descrição do case"
                      value={c.description}
                      onChange={(e) => updateCase(i, 'description', e.target.value)}
                      rows="2"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm outline-none focus:border-neutral-500 resize-none"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Equipe (Trainers IE) */}
            <div className="border-t border-neutral-200 pt-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label>Eneagramados (Trainers IE)</Label>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    Aparecem na página /equipe e no perfil da franquia.
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addTrainer} data-testid="franchise-add-trainer">
                  <iconify-icon icon="solar:user-plus-linear" className="mr-1"></iconify-icon>
                  Adicionar
                </Button>
              </div>
              <div className="space-y-4">
                {formData.team.map((m, i) => (
                  <div
                    key={m._uid || `m-${i}`}
                    className="p-4 bg-neutral-50 rounded-lg relative grid grid-cols-1 md:grid-cols-[140px_1fr] gap-4"
                    data-testid={`franchise-trainer-row-${i}`}
                  >
                    <button
                      type="button"
                      onClick={() => removeTrainer(i)}
                      className="absolute top-2 right-2 p-1 hover:bg-red-100 text-red-600 rounded"
                      aria-label="Remover trainer"
                      data-testid={`franchise-trainer-remove-${i}`}
                    >
                      <iconify-icon icon="solar:trash-bin-linear"></iconify-icon>
                    </button>
                    <div>
                      <Label className="text-xs">Foto</Label>
                      <FileUploader
                        value={m.photo}
                        onChange={(url) => updateTrainer(i, 'photo', url)}
                        accept="image"
                        folder="team"
                        label="Foto do trainer"
                        testId={`franchise-trainer-photo-${i}`}
                      />
                    </div>
                    <div className="space-y-2 pr-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor={`trainer-name-${i}`} className="text-xs">Nome *</Label>
                          <Input
                            id={`trainer-name-${i}`}
                            placeholder="Heloisa Labres"
                            value={m.name}
                            onChange={(e) => updateTrainer(i, 'name', e.target.value)}
                            data-testid={`franchise-trainer-name-${i}`}
                          />
                        </div>
                        <div>
                          <Label htmlFor={`trainer-role-${i}`} className="text-xs">Função</Label>
                          <Input
                            id={`trainer-role-${i}`}
                            placeholder="Trainer / Head Trainer / Sócio"
                            value={m.role || ''}
                            onChange={(e) => updateTrainer(i, 'role', e.target.value)}
                            data-testid={`franchise-trainer-role-${i}`}
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor={`trainer-ig-${i}`} className="text-xs">Instagram (opcional)</Label>
                        <Input
                          id={`trainer-ig-${i}`}
                          placeholder="@nome_eneagramado"
                          value={m.instagram || ''}
                          onChange={(e) => updateTrainer(i, 'instagram', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {formData.team.length === 0 && (
                  <p className="text-sm text-neutral-500 italic">
                    Nenhum Eneagramado cadastrado nesta franquia.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-neutral-200">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="franchise-form-submit">
                {editingFranchise ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FranchisesManager;
